// T016: UI Store

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { BattlePosition } from '../types';

type Screen = 'auth' | 'mainMenu' | 'lobby' | 'deckBuilder' | 'game' | 'victory' | 'replay' | 'history' | 'admin' | 'shop' | 'quests';
type ModalType = 'cardDetail' | 'settings' | 'confirm' | null;
export type GameMode = 'local' | 'online';

interface UIStore {
  // Navigation
  currentScreen: Screen;
  previousScreen: Screen | null;

  // Modal
  activeModal: ModalType;
  modalData: unknown;

  // Game UI state
  selectedCardId: string | null;
  highlightedPositions: BattlePosition[];
  hoveredCardId: string | null;

  // 遊戲模式
  gameMode: GameMode;

  // Tactics phase state
  tacticsOption: 'attack' | 'deploy' | null;
  selectedAttackers: string[];
  // Unit reposition: tracks the source position for swap/move
  unitMoveSource: BattlePosition | null;

  // Actions
  navigateTo: (screen: Screen) => void;
  goBack: () => void;

  // Modal actions
  openModal: (type: ModalType, data?: unknown) => void;
  closeModal: () => void;

  // 遊戲模式
  setGameMode: (mode: GameMode) => void;

  // Card selection
  selectCard: (cardId: string | null) => void;
  hoverCard: (cardId: string | null) => void;
  highlightPositions: (positions: BattlePosition[]) => void;
  clearHighlights: () => void;

  // Tactics actions
  setTacticsOption: (option: 'attack' | 'deploy' | null) => void;
  toggleAttacker: (cardId: string) => void;
  addAttacker: (cardId: string) => void;
  removeAttacker: (cardId: string) => void;
  clearAttackers: () => void;
  setUnitMoveSource: (position: BattlePosition | null) => void;

  // Reset
  resetUIState: () => void;
}

export const useUIStore = create<UIStore>()(
  devtools(
    (set, get) => ({
      currentScreen: 'mainMenu',
      previousScreen: null,
      activeModal: null,
      modalData: null,
      selectedCardId: null,
      highlightedPositions: [],
      hoveredCardId: null,
      gameMode: 'local' as GameMode,
      tacticsOption: null,
      selectedAttackers: [],
      unitMoveSource: null,

      navigateTo: (screen) => {
        const { currentScreen } = get();
        set({
          previousScreen: currentScreen,
          currentScreen: screen,
        });
      },

      goBack: () => {
        const { previousScreen } = get();
        if (previousScreen) {
          set({
            currentScreen: previousScreen,
            previousScreen: null,
          });
        }
      },

      setGameMode: (mode) => {
        set({ gameMode: mode });
      },

      openModal: (type, data) => {
        set({
          activeModal: type,
          modalData: data,
        });
      },

      closeModal: () => {
        set({
          activeModal: null,
          modalData: null,
        });
      },

      selectCard: (cardId) => {
        set({ selectedCardId: cardId });
      },

      hoverCard: (cardId) => {
        set({ hoveredCardId: cardId });
      },

      highlightPositions: (positions) => {
        set({ highlightedPositions: positions });
      },

      clearHighlights: () => {
        set({ highlightedPositions: [] });
      },

      setTacticsOption: (option) => {
        set({
          tacticsOption: option,
          selectedAttackers: option === 'attack' ? get().selectedAttackers : [],
        });
      },

      toggleAttacker: (cardId) => {
        const { selectedAttackers } = get();
        if (selectedAttackers.includes(cardId)) {
          set({
            selectedAttackers: selectedAttackers.filter((id) => id !== cardId),
          });
        } else {
          set({
            selectedAttackers: [...selectedAttackers, cardId],
          });
        }
      },

      addAttacker: (cardId) => {
        const { selectedAttackers } = get();
        if (!selectedAttackers.includes(cardId)) {
          set({ selectedAttackers: [...selectedAttackers, cardId] });
        }
      },

      removeAttacker: (cardId) => {
        const { selectedAttackers } = get();
        set({
          selectedAttackers: selectedAttackers.filter((id) => id !== cardId),
        });
      },

      clearAttackers: () => {
        set({ selectedAttackers: [] });
      },

      setUnitMoveSource: (position) => {
        set({ unitMoveSource: position });
      },

      resetUIState: () => {
        set({
          selectedCardId: null,
          highlightedPositions: [],
          hoveredCardId: null,
          tacticsOption: null,
          selectedAttackers: [],
          unitMoveSource: null,
        });
      },
    }),
    { name: 'UIStore' }
  )
);
